import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Body, Button, Card, Heading, I, InfoCallout, Stack } from '@leanin/ui';
import { FlowShell, type FlowStep } from '@/components/onboarding/FlowShell';
import { trackOnboardingStepCompleted } from '@/lib/analytics/events';
import { flowStepParams } from '@/lib/analytics/onboarding-steps';
import { Picture } from '@/components/media/Picture';
import resourcesImg from '@/assets/onboarding/resources.png?w=440;880;1320&format=avif;webp;png&as=picture';

// Lean In Member onboarding tour — step 4 of 5.
// Mirrors the CM Build-leadership-skills step: lilac intro callout +
// three short paragraphs framing the Resources library, followed by
// an annotated screenshot. The Circle references in the body assume
// the member is heading toward joining a Circle (see step 2 of the
// flow) — the LI flow has already surfaced Circles as a pathway.

const STEPS: ReadonlyArray<FlowStep> = [
  { label: 'Welcome' },
  { label: 'Lean In Circles' },
  { label: 'Get involved' },
  { label: 'Build leadership skills' },
  { label: "You're in" },
];

export const Route = createFileRoute('/onboarding/build-skills')({
  component: OnboardingBuildSkillsPage,
});

function OnboardingBuildSkillsPage() {
  const navigate = useNavigate();
  return (
    <FlowShell
      role="Lean In Member"
      stepIndex={3}
      steps={STEPS}
      minutes={2}
      title="Explore our resource."
      primary={
        <Button
          variant="primary"
          iconRight={I.arrow}
          onClick={() => {
            trackOnboardingStepCompleted(
              flowStepParams('/onboarding/build-skills'),
            );
            navigate({ to: '/onboarding/complete' });
          }}
        >
          Next
        </Button>
      }
      secondary={
        <Button
          variant="ghost"
          onClick={() => navigate({ to: '/onboarding/get-involved' })}
        >
          Back
        </Button>
      }
    >
      <Stack gap="lg">
        <Heading level={2}>Skill-building resources to help you grow</Heading>
        <InfoCallout tone="lilac" pad="default">
          <strong>Lean In Connect</strong> is a community designed to move
          your career forward and we have hundreds of free skill-building
          resources to help you do that.
        </InfoCallout>

        <Stack gap="md">
          <Body size="md">
            Our leadership library includes{' '}
            <strong style={{ color: 'var(--primary)' }}>120+ topics</strong>,
            each with discussion questions for your Circle, AI prompts to
            help you explore ideas more deeply, and practical strategies you
            can put into action right away.
          </Body>
          <Body size="md">
            You can explore on your own, but research shows we learn best in
            groups. Each month in your Circle, you’ll discuss one of these
            topics together.
          </Body>
          <Body size="md">
            And the impact is real:{' '}
            <strong style={{ color: 'var(--primary)' }}>
              Circle members are more than 70% more likely to regularly
              receive career information and resources.
            </strong>{' '}
            We’re excited to support you as you learn, grow, and lead.
          </Body>
        </Stack>

        <Card pad="flush" clip>
          <Picture
            image={resourcesImg}
            alt="Lean In Resources library page showing the search, filter tabs, and a grid of leadership-skill resource cards"
            sizes="(max-width: 720px) 100vw, 880px"
            style={{ width: '100%', height: 'auto', display: 'block' }}
          />
        </Card>
      </Stack>
    </FlowShell>
  );
}
